import {
  Alert,
  Box,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

export interface BillingCustomerMatch {
  id: string;
  commonName: string;
  matchKind: string;
  status?: string;
  linkable: boolean;
  organizationId?: string | null;
  portalUrl?: string;
}

export interface BillingNameMatchResult {
  status: "DISABLED" | "ANSWERED" | "UNAVAILABLE";
  items: BillingCustomerMatch[];
}

export interface BillingSyncResult {
  status:
    | "DISABLED"
    | "CREATED"
    | "LINKED"
    | "LINK_REFUSED_CREATED"
    | "ALREADY_REGISTERED"
    | "FAILED";
  customerId?: string | null;
  code?: string | null;
}

const NEW_CUSTOMER = "__new__";
const HEADING_ID = "billing-customer-matches-heading";

function isWebUrl(url?: string): url is string {
  return !!url && /^https?:\/\//i.test(url);
}

export default function BillingCustomerMatches({
  result,
  loading,
  selectedCustomerId,
  onSelect,
}: {
  result?: BillingNameMatchResult;
  loading: boolean;
  selectedCustomerId: string | null;
  onSelect: (customerId: string | null) => void;
}) {
  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary" mt={2}>
        Checking billing for existing customers...
      </Typography>
    );
  }
  if (!result || result.status === "DISABLED") {
    return null;
  }
  if (result.status === "UNAVAILABLE") {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Could not check billing for existing customers. A new billing customer
        will be registered for this organization.
      </Alert>
    );
  }
  if (result.items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" mt={2}>
        No existing billing customer matches this name. A new one will be
        registered.
      </Typography>
    );
  }

  return (
    <Box mt={2}>
      <Typography variant="subtitle2" id={HEADING_ID}>
        This may already be a billing customer
      </Typography>
      <RadioGroup
        aria-labelledby={HEADING_ID}
        value={selectedCustomerId ?? NEW_CUSTOMER}
        onChange={(_e, value) =>
          onSelect(value === NEW_CUSTOMER ? null : value)
        }
      >
        <FormControlLabel
          value={NEW_CUSTOMER}
          control={<Radio size="small" />}
          label="Create a new billing customer"
        />
        {result.items.map((match) => (
          <Box key={match.id} display="flex" alignItems="center">
            <FormControlLabel
              value={match.id}
              disabled={!match.linkable}
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2">
                    Link to {match.commonName} ({match.id})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {match.matchKind === "exact" ? "Same name" : "Similar name"}
                    {match.status ? `, ${match.status}` : ""}
                    {match.linkable
                      ? ""
                      : ", already linked to another organization"}
                  </Typography>
                </Box>
              }
            />
            {isWebUrl(match.portalUrl) && (
              <Tooltip title="Open in billing">
                <IconButton
                  size="small"
                  component="a"
                  href={match.portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${match.commonName} in billing`}
                >
                  <OpenInNewIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ))}
      </RadioGroup>
    </Box>
  );
}

export function billingSyncMessage(
  result?: BillingSyncResult | null
): { severity: "success" | "warning"; text: string } | null {
  const customer = result?.customerId
    ? `billing customer ${result.customerId}`
    : null;
  switch (result?.status) {
    case "CREATED":
      return {
        severity: "success",
        text: `Registered ${
          customer ?? "a new billing customer"
        } for this organization.`,
      };
    case "LINKED":
      return {
        severity: "success",
        text: `Linked this organization to ${
          customer ?? "the chosen billing customer"
        }.`,
      };
    case "LINK_REFUSED_CREATED":
      return {
        severity: "warning",
        text: `The chosen billing customer could not be linked${
          result.code ? ` (${result.code})` : ""
        }, so ${
          customer ? `new ${customer}` : "a new billing customer"
        } was registered instead.`,
      };
    case "FAILED":
      return {
        severity: "warning",
        text: "The organization was created, but billing could not be updated. Link it from the billing portal.",
      };
    default:
      return null;
  }
}
